import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ShoppingCart,
  AccessTime,
  LocationOn,
  Info,
  CheckCircle,
  Cancel,
  HourglassEmpty,
} from "@mui/icons-material";
import { ProductPurchase } from "./type";

interface StateDisplay {
  text: string;
  icon: React.ReactNode;
  chipColor?:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
}

// Helper functions with proper TypeScript typing
const getAcknowledgementState = (state?: number): StateDisplay => {
  switch (state) {
    case 0:
      return {
        text: "Yet to be acknowledged",
        icon: <HourglassEmpty color="warning" />,
      };
    case 1:
      return { text: "Acknowledged", icon: <CheckCircle color="success" /> };
    default:
      return { text: "Unknown", icon: <Info color="disabled" /> };
  }
};

const getConsumptionState = (state?: number): StateDisplay => {
  switch (state) {
    case 0:
      return {
        text: "Yet to be consumed",
        icon: <HourglassEmpty color="warning" />,
      };
    case 1:
      return { text: "Consumed", icon: <CheckCircle color="success" /> };
    default:
      return { text: "Unknown", icon: <Info color="disabled" /> };
  }
};

const getPurchaseState = (state?: number): StateDisplay => {
  switch (state) {
    case 0:
      return {
        text: "Purchased",
        icon: <CheckCircle color="success" />,
        chipColor: "success",
      };
    case 1:
      return {
        text: "Canceled",
        icon: <Cancel color="error" />,
        chipColor: "error",
      };
    case 2:
      return {
        text: "Pending",
        icon: <HourglassEmpty color="warning" />,
        chipColor: "warning",
      };
    default:
      return {
        text: "Unknown",
        icon: <Info color="disabled" />,
        chipColor: "default",
      };
  }
};

const getPurchaseType = (type?: number): StateDisplay => {
  switch (type) {
    case 0:
      return { text: "Test", icon: <Info color="info" /> };
    case 1:
      return { text: "Promo", icon: <Info color="secondary" /> };
    case 2:
      return { text: "Rewarded", icon: <Info color="primary" /> };
    default:
      return { text: "Standard", icon: <ShoppingCart color="disabled" /> };
  }
};

const formatDate = (timeMillis?: string | number): string => {
  if (!timeMillis) return "N/A";
  const date = new Date(
    typeof timeMillis === "string" ? parseInt(timeMillis) : timeMillis
  );
  return date.toLocaleString();
};

interface ProductPurchaseDisplayProps {
  purchase: ProductPurchase;
}

/**
 * Component to display Android Publisher API product purchase information
 */
const AndrodiVerifyDetails: React.FC<ProductPurchaseDisplayProps> = ({
  purchase,
}) => {
  if (!purchase) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1">No purchase data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const purchaseStateInfo = getPurchaseState(purchase.purchaseState);
  const purchaseTypeInfo = getPurchaseType(purchase.purchaseType);
  const acknowledgementStateInfo = getAcknowledgementState(
    purchase.acknowledgementState
  );
  const consumptionStateInfo = getConsumptionState(purchase.consumptionState);

  return (
    <Card elevation={3} sx={{ maxWidth: 800, margin: "0 auto" }}>
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5">
          <ShoppingCart sx={{ mr: 1, verticalAlign: "middle" }} />
          Android Purchase Details
        </Typography>
        <Chip
          icon={purchaseStateInfo.icon as any}
          label={purchaseStateInfo.text}
          color={purchaseStateInfo.chipColor}
          variant="filled"
        />
      </Box>

      <CardContent>
        <Grid container spacing={3}>
          {/* Product Information */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Product Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Product ID
                </Typography>
                <Typography variant="body1">
                  {purchase.productId || "N/A"}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Order ID
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {purchase.orderId || "N/A"}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Purchase Type
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  {purchaseTypeInfo.icon}
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    {purchaseTypeInfo.text}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Purchase Time
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <AccessTime color="action" />
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    {formatDate(purchase.purchaseTimeMillis)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Status Information */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Status Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell>Status Type</TableCell>
                    <TableCell>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Acknowledgement State</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {acknowledgementStateInfo.icon}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {acknowledgementStateInfo.text}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Consumption State</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {consumptionStateInfo.icon}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {consumptionStateInfo.text}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Quantity</TableCell>
                    <TableCell>{purchase.quantity || 1}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Refundable Quantity</TableCell>
                    <TableCell>
                      {purchase.refundableQuantity !== undefined
                        ? purchase.refundableQuantity
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Region Code</TableCell>
                    <TableCell>
                      {purchase.regionCode ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <LocationOn color="action" fontSize="small" />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {purchase.regionCode}
                          </Typography>
                        </Box>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Technical Details */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Technical Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Purchase Token
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {purchase.purchaseToken || "N/A"}
                </Typography>
              </Grid>
              {purchase.developerPayload && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Developer Payload
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {purchase.developerPayload}
                  </Typography>
                </Grid>
              )}
              {purchase.obfuscatedExternalAccountId && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Obfuscated Account ID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {purchase.obfuscatedExternalAccountId}
                  </Typography>
                </Grid>
              )}
              {purchase.obfuscatedExternalProfileId && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Obfuscated Profile ID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {purchase.obfuscatedExternalProfileId}
                  </Typography>
                </Grid>
              )}
              {purchase.kind && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Kind
                  </Typography>
                  <Typography variant="body2">{purchase.kind}</Typography>
                </Grid>
              )}
              {purchase.etag && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    ETag
                  </Typography>
                  <Typography variant="body2">{purchase.etag}</Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AndrodiVerifyDetails;
